import { invoiceServices } from "@services/apis/Invoice";
import { GetOfframpBanksData } from "@services/models/InvoiceApprovals/GetOfframpBanksModel";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import AgGridReactTable from "../../components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "../../components/ag-grid/AgGridSkeleton";
import CopyButton from "../../components/buttons/CopyButton";
import BankManagementSlider from "./components/BankManagementSlider";
import EditBankSlider from "./components/EditBankSlider";
import StatusPillBankManagement from "./components/StatusPillBankManagement";

interface BankCompanyOwnedProps {
  addBankSliderOpen: boolean;
  setAddBankSliderOpen: (open: boolean) => void;
}

const BankCompanyOwned: React.FC<BankCompanyOwnedProps> = ({
  addBankSliderOpen,
  setAddBankSliderOpen,
}) => {
  const gridRef = useRef<AgGridReact>(null);

  const [bankData, setBankData] = useState<GetOfframpBanksData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [editBankSliderOpen, setEditBankSliderOpen] = useState<boolean>(false);
  const [selectedBank, setSelectedBank] = useState<GetOfframpBanksData | null>(
    null
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Bank ID",
        field: "bankId",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Bank Name",
        field: "bankName",
        flex: 1.5,
        minWidth: 150,
        maxWidth: 250,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Account Number",
        field: "accountNo",
        flex: 1.5,
        minWidth: 150,
        maxWidth: 250,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-2 h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.value}
              </span>
              <CopyButton
                copyText={params.value}
                copyMessage="Account number copied!"
                copyIconSize={10}
                buttonSize="16px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Account Name",
        field: "accountName",
        flex: 1.5,
        minWidth: 150,
        maxWidth: 250,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Account Type",
        field: "accountType",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Status",
        field: "isActive",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <StatusPillBankManagement
              statusText={params.data.isActive ? "ACTIVE" : "DEACTIVATED"}
            />
          );
        },
      },
      {
        headerName: "Edit",
        field: "id",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        flex: 0.8,
        minWidth: 60,
        maxWidth: 220,
        filter: null,
        cellRenderer: (params: any) => {
          return (
            <div className="flex justify-start">
              <div
                className="flex justify-center items-center cursor-pointer shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] bg-border-button-secondary border border-bg-button-secondary"
                style={{ width: "20px", height: "20px", borderRadius: "4px" }}
                onClick={() => {
                  setSelectedBank(params.data);
                  setEditBankSliderOpen(true);
                }}
              >
                <span className="text-[14px] material-symbols-outlined text-text-base">
                  border_color
                </span>
              </div>
            </div>
          );
        },
      },
    ],
    []
  );

  const fetchBankData = async () => {
    try {
      setLoading(true);
      const response = await invoiceServices.getOfframpBanks();
      setBankData(response.data);
    } catch (err: any) {
      setError("Failed to fetch bank data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankData();
  }, []);

  return (
    <>
      <div className="flex flex-col bg-bg-page-body py-[20px] h-[85vh]">
        {loading ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "calc(100vh - 110px)", width: "100%" }}
          />
        ) : (
          <AgGridReactTable
            ref={gridRef}
            rowData={bankData}
            columnDefs={columnDefs}
            getRowId={(params) => params.data.bankId?.toString()}
            error={error}
            style={{ height: "calc(100vh - 110px)", width: "100%" }}
          />
        )}
      </div>
      <BankManagementSlider
        isOpen={addBankSliderOpen}
        onClose={() => setAddBankSliderOpen(false)}
        onBankAdded={() => fetchBankData()}
      />
      <EditBankSlider
        isOpen={editBankSliderOpen}
        onClose={() => {
          setEditBankSliderOpen(false);
          setSelectedBank(null);
        }}
        onBankUpdated={() => fetchBankData()}
        bankData={selectedBank}
      />
    </>
  );
};

export default BankCompanyOwned;
